import { format, parseISO } from 'date-fns';
import { t } from '@/lib/i18n';
import {
  getLocalePrefs,
  intlLocaleForCountry,
  kgToDisplay,
  weightUnitLabel,
  type DateFormatPref,
} from '@/lib/locale';

function regionalLocale(): string {
  return intlLocaleForCountry(getLocalePrefs().country);
}

function datePattern(pref: DateFormatPref): string {
  switch (pref) {
    case 'mdy':
      return 'MM/dd/yyyy';
    case 'ymd':
      return 'yyyy-MM-dd';
    default:
      return 'dd/MM/yyyy';
  }
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const prefs = getLocalePrefs();
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(parseISO(value), datePattern(prefs.dateFormat));
    }
    return new Intl.DateTimeFormat(regionalLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: prefs.timezone,
    }).format(parseISO(value));
  } catch {
    return value;
  }
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const prefs = getLocalePrefs();
    const hour12 = prefs.timeFormat === 'h12';
    return new Intl.DateTimeFormat(regionalLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12,
      timeZone: prefs.timezone,
    }).format(parseISO(value));
  } catch {
    return value;
  }
}

export function formatTime(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  try {
    const prefs = getLocalePrefs();
    const date = typeof value === 'string' ? parseISO(value) : value;
    return new Intl.DateTimeFormat(regionalLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: prefs.timeFormat === 'h12',
      timeZone: prefs.timezone,
    }).format(date);
  } catch {
    return '—';
  }
}

/** Data de hoje no formato ISO `AAAA-MM-DD` no fuso da conta. */
export function todayIso(): string {
  const prefs = getLocalePrefs();
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: prefs.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    /* fallback */
  }
  return format(new Date(), 'yyyy-MM-dd');
}

/** Converte ISO `AAAA-MM-DD` para exibição local. */
export function isoToBr(iso: string | null | undefined): string {
  if (!iso) return '';
  return formatDate(iso);
}

export function dateInputPlaceholder(pref: DateFormatPref = getLocalePrefs().dateFormat): string {
  switch (pref) {
    case 'mdy':
      return 'MM/DD/AAAA';
    case 'ymd':
      return 'AAAA-MM-DD';
    default:
      return 'DD/MM/AAAA';
  }
}

/** Máscara de data a partir de dígitos, no formato preferido da conta. */
export function maskDateInput(raw: string, pref: DateFormatPref = getLocalePrefs().dateFormat): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (pref === 'ymd') {
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  }
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
}

/** Interpreta data mascarada e devolve ISO `AAAA-MM-DD`, ou null se incompleta/inválida. */
export function parseMaskedDate(
  masked: string,
  pref: DateFormatPref = getLocalePrefs().dateFormat,
): string | null {
  const digits = masked.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  let year: number;
  let month: number;
  let day: number;
  if (pref === 'ymd') {
    year = Number(digits.slice(0, 4));
    month = Number(digits.slice(4, 6));
    day = Number(digits.slice(6, 8));
  } else if (pref === 'mdy') {
    month = Number(digits.slice(0, 2));
    day = Number(digits.slice(2, 4));
    year = Number(digits.slice(4, 8));
  } else {
    day = Number(digits.slice(0, 2));
    month = Number(digits.slice(2, 4));
    year = Number(digits.slice(4, 8));
  }
  if (!isValidYmd(year, month, day)) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Formata peso para exibição (kg armazenado → unidade preferida). */
export function formatWeight(kg: number | null | undefined): string {
  if (kg == null) return '—';
  const prefs = getLocalePrefs();
  const display = kgToDisplay(kg, prefs.weightUnit);
  const unit = weightUnitLabel(prefs.weightUnit);
  return `${display.toLocaleString(regionalLocale(), { maximumFractionDigits: 1 })} ${unit}`;
}

export function formatMoney(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  const prefs = getLocalePrefs();
  return new Intl.NumberFormat(intlLocaleForCountry(prefs.country), {
    style: 'currency',
    currency: prefs.currency,
  }).format(amount);
}

export function sexLabel(sex: 'male' | 'female'): string {
  return sex === 'male' ? t('sex.male') : t('sex.female');
}

export function sexSymbol(sex: 'male' | 'female'): string {
  return sex === 'male' ? '♂' : '♀';
}

const eventTypeKeys = {
  weighing: 'event.weighing',
  weaning: 'event.weaning',
  natural_mount: 'event.natural_mount',
  insemination: 'event.insemination',
  pregnancy_diagnosis: 'event.pregnancy_diagnosis',
  birth: 'event.birth',
  milk_weighing: 'event.milk_weighing',
  medication: 'event.medication',
  milestone: 'event.milestone',
} as const;

export function eventTypeLabel(type: string): string {
  const key = eventTypeKeys[type as keyof typeof eventTypeKeys];
  return key ? t(key) : type;
}

const movementTypeKeys = {
  transfer: 'movement.transfer',
  sale: 'movement.sale',
  death: 'movement.death',
} as const;

export function movementTypeLabel(type: string): string {
  const key = movementTypeKeys[type as keyof typeof movementTypeKeys];
  return key ? t(key) : type;
}

export type BreedLabelInput = {
  abbreviation?: string | null;
  name?: string | null;
};

export function breedLabel(breed: BreedLabelInput | null | undefined): string {
  if (!breed) return '—';
  const abbr = breed.abbreviation?.trim();
  const name = breed.name?.trim();
  if (abbr && name) return `${abbr} - ${name}`;
  if (abbr) return abbr;
  if (name) return name;
  return '—';
}
